import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { cloneDeep } from 'lodash';
import { Observable, BehaviorSubject, firstValueFrom, lastValueFrom, of } from 'rxjs';
import { ConfigurationLayerModel, ConfigurationModel } from '../models/configuration';
import { CoordenateService } from './coordenate.service';
import { NavaidModel } from '../models/navaid';
import { EndpointsService } from './endpoints.service';
import { ModalControlService } from './modal-control.service';
import { UiBlockService } from './ui-block.service';
import { UserSettingsService } from './userSettings.service';
import { ConfigurationTerrain, TerrainTypeModel } from '../models/terrains';
import { ModalConfirmationService } from './modal-confirmation.service';
import { FormGroup, NgForm } from '@angular/forms';


@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  public active: boolean;
  public activeTerrain: boolean;
  public configurationList: ConfigurationModel[];
  public currentConfiguration: ConfigurationModel;
  public tempConfiguration: ConfigurationModel;
  public selectedRow: number = -1;

  public savedTerrains$ = new BehaviorSubject<ConfigurationTerrain[]>([]);
  public terrainTypes: TerrainTypeModel[];
  public terrainToEdit: ConfigurationTerrain;

  public terrainEditMode: boolean;

  constructor(private http: HttpClient,
    private endpointService: EndpointsService,
    private modalService: ModalControlService,
    private uiBlock: UiBlockService,
    public coordenateService: CoordenateService,
    private userSettingsService: UserSettingsService,
    private readonly modelConfirmationMessage: ModalConfirmationService,
    ) {

  }

  validateTerrainEditOrDelete(terrainToValidate: ConfigurationTerrain, indexToExclude : number){
    const dted1Type: TerrainTypeModel = this.terrainTypes.find(terrainType => terrainType.type == "DTED1");
    if (terrainToValidate.terrainTypeId != dted1Type.id){
      return true;
    }
    // check if at least one DTED1 Terrain is active
    for (let i = 0; i<this.savedTerrains$.getValue().length; i++){
      if (i != indexToExclude && this.savedTerrains$.getValue()[i].terrainTypeId == dted1Type.id && this.savedTerrains$.getValue()[i].active){
        return true
      }
    }
    return false;
  }

  async validateTerrain(terrainId: number){
    if (await this.getTerrainValidationInProgress(terrainId)){
      this.modalService.showMesssageModal("Terrains Error", "You are already validating this terrain. Please wait for the validation to finish.")
      return;
    }
    return firstValueFrom(this.http.get<void>(
      this.endpointService.endpointValidateTerrain + "/" + terrainId,
      this.endpointService.httpOptions
    ));
  };

  updateTerrain(newTerrain: FormData){
    return this.http.put<ConfigurationTerrain>(
      this.endpointService.apiUrlTerrains,
      newTerrain,
      this.endpointService.httpOptions
    ).toPromise();
  };

  saveTerrain(newTerrain: FormData, isUpdate: boolean){
    return this.http.post<void>(
      this.endpointService.apiUrlTerrains + `/${isUpdate}`,
      newTerrain,
      this.endpointService.httpOptions
    ).toPromise();
  };

  deleteTerrain(terrainId: number, projectId?: number) {
    if (!projectId){
      projectId = this.currentConfiguration.projectId;
    }
    return this.http
      .delete<boolean>(
        this.endpointService.apiUrlTerrains + '/' + terrainId,
        {
          headers : this.endpointService.httpOptions.headers,
          params : {
            "projectId" : projectId,
          }
        }
      ).toPromise();
  }

  fetchAllTerrains(){
    return this.http.get<ConfigurationTerrain[]>(
      this.endpointService.apiUrlTerrains,
      this.endpointService.httpOptions
    )
  }

  async fetchTerrainsByProjectPromiseRequest(projectId : number){
    const terrains: ConfigurationTerrain[] = await this.http.get<ConfigurationTerrain[]>(
      this.endpointService.apiUrlTerrains + '/' + projectId,
      this.endpointService.httpOptions
    ).toPromise();
    this.savedTerrains$.next(terrains.sort((a, b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0)) as ConfigurationTerrain[]);
  }

  async fetchTerrainsByProject(projectId: number) {
    const savedTerrains: ConfigurationTerrain[] = await this.http.get<ConfigurationTerrain[]>(
      this.endpointService.apiUrlTerrains + '/' + projectId,
      this.endpointService.httpOptions
    ).toPromise();
    // Create a new sorted array reference
    const sortedTerrains = [...savedTerrains].sort((a, b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
    this.savedTerrains$.next(sortedTerrains);
  }

  fetchTerrainById(terrainId: number){
    return this.http.get<ConfigurationTerrain>(
      this.endpointService.endpointFetchTerrainById + `/${terrainId}`,
      this.endpointService.httpOptions
    ).toPromise();
  };

  async fetchTerrainTypesPromiseRequest(){
    const terrainTypes:TerrainTypeModel[] = await this.http.get<TerrainTypeModel[]>(
      this.endpointService.endpointGetTerrainTypes,
      this.endpointService.httpOptions
    ).toPromise()
    this.terrainTypes =  terrainTypes.sort((a, b) => (a.id > b.id) ? 1 : ((b.id > a.id) ? -1 : 0));
  }


  fetchTerrainTypes(){
    this.http.get<TerrainTypeModel[]>(
      this.endpointService.endpointGetTerrainTypes,
      this.endpointService.httpOptions
    ).subscribe((terrainTypes: TerrainTypeModel[]) => {
      terrainTypes.sort((a, b) => (a.id > b.id) ? 1 : ((b.id > a.id) ? -1 : 0));
      this.terrainTypes = terrainTypes;
    });
  }


  async getTerrainResolution(formData: FormData): Promise<number>{
    return await this.http
      .post<number>(
        this.endpointService.endpointGetTerrainResolution,
        formData,
        this.endpointService.httpOptions
      ).toPromise();
  }

  async getTerrainChunkSize(): Promise<number>{
    return await firstValueFrom(this.http.get<number>(
      this.endpointService.endpointGetChunkSize,
      this.endpointService.httpOptions)
    )
  }

  async getTerrainUploadInProgress(): Promise<boolean>{
    return await firstValueFrom(this.http.get<boolean>(
      this.endpointService.endpointGetTerrainUploadInProgress,
      this.endpointService.httpOptions)
    )
  }

  async isUserPerformingCalculations(projectId: number): Promise<boolean>{
    return await firstValueFrom(this.http.get<boolean>(
      this.endpointService.isUserPerformingCalculations + `/${projectId}`,
      this.endpointService.httpOptions)
    )
  }

  async getTerrainValidationInProgress(terrainId: number): Promise<boolean>{
    return await firstValueFrom(this.http.get<boolean>(
      this.endpointService.endpointGetTerrainValidationInProgress + `/${terrainId}`,
      this.endpointService.httpOptions)
    )
  }


  async deleteTerrainUploadInProgress(): Promise<void>{
    return await firstValueFrom(this.http.delete<void>(
      this.endpointService.endpointDeleteTerrainUploadInProgress,
      this.endpointService.httpOptions)
    )
  }

  getSelectedRow(): number{
    return this.selectedRow;
  }

  setSelectedRow(i : number): void{
    this.selectedRow = i;
  }

  public loadConfigurationsOfProject(projectId : number): void{
    this.getAllConfigurations(projectId).subscribe((data) => {
      this.selectedRow = -1;
      this.configurationList = [];
      for(var i = 0; i < data.length; i++){
        this.configurationList.push(new ConfigurationModel(data[i]));
        if(data[i].active){
          this.getConfiguration(data[i].id).subscribe((config) => {
            this.currentConfiguration = ConfigurationModel.map(config);
                this.updateCoordenateControlRCSList();
          },(error) => {
            console.log(error);
          });
        }
      }
    },
    (error) => {
      console.log(error);
    });
  }

  updateCoordenateControlRCSList(){
    this.coordenateService.rcsList = [];
    this.currentConfiguration.rcs.forEach(el => {
      if(el.enabled)
      {
        this.coordenateService.rcsList.push(el);
        if(el.epsg == this.userSettingsService.userSettings.epsg){
           this.coordenateService.setRCSActive(el);
        }
      }
    })
  }

  getConfiguration(id : number) : Observable<ConfigurationModel>{
    return this.http.get<ConfigurationModel>(
      this.endpointService.apiUrlConfiguration + '/' + id,
      this.endpointService.httpOptions
      )
  }

  getActiveConfiguration() : Observable<ConfigurationModel>{
    return this.http.get<ConfigurationModel>(
      this.endpointService.apiUrlConfiguration + '/active',
      this.endpointService.httpOptions
      )
  }

  getAllConfigurations(projectId : number) : Observable<ConfigurationModel[]>{
    return this.http.get<ConfigurationModel[]>(
      this.endpointService.apiUrlConfiguration + '/list/' + projectId,
      this.endpointService.httpOptions
      )
  }

  deleteConfiguration(id: number): Observable<boolean> {
    return this.http
      .delete<boolean>(
        this.endpointService.apiUrlConfiguration + '/' + id,
        this.endpointService.httpOptions
      );
  }

  activateConfiguration(idConfig: number): Observable<boolean>{
    return this.http.post<boolean>(this.endpointService.apiUrlConfiguration + "/activate/" + idConfig, null, this.endpointService.httpOptions);
  }

  cancelConfigurationPopup():void{
    this.redoChanges();
  }

  async deleteCalculationsDatabaseRequest(projectId: number): Promise<number> {
    try {
      const response = await firstValueFrom(
        this.http.delete<void>(
          `${this.endpointService.deleteCalculationsDatabase}/${projectId}`,
          {
            ...this.endpointService.httpOptions,
            observe: 'response' // Observe the full HTTP response
          }
        )
      );
      return response.status;
    } catch (error: any) {
      if (error.status) {
        return error.status;
      }
    }
  }

  async checkIfCalculationsDatabaseExists(projectId: number): Promise<boolean> {
    const response = await firstValueFrom(
      this.http.get<boolean>(
        `${this.endpointService.checkCalculationsDatabase}/${projectId}`,
        this.endpointService.httpOptions)
    );
    return response;
  }


  async getCalculationsDatabaseUserSpaceDTO(projectId: number): Promise<number> {
    try {
      const response = await firstValueFrom(
        this.http.get<void>(
          `${this.endpointService.deleteCalculationsDatabase}/${projectId}`,
          {
            ...this.endpointService.httpOptions,
            observe: 'response' // Observe the full HTTP response
          }
        )
      );
      return response.status;
    } catch (error: any) {
      if (error.status) {
        return error.status;
      }
    }
  }

  async deleteCalculationsDatabase(message: string = null) {
    const projectId: number = this.currentConfiguration.projectId;
    if (!(await this.checkIfCalculationsDatabaseExists(projectId))) {
      return true;
    }
    
    let messageToDisplay = "In order to prevent errors in the results the Calculation Database needs to be deleted. Do you want to continue?";
    if (message) {
      messageToDisplay = message;
    }
    let success = false;
    await this.modelConfirmationMessage.
      showMesssageConfirmationModalPromise("Calculation Database",
        messageToDisplay,
        "OK", "Cancel",
        async () => {
          const responseStatus = await this.deleteCalculationsDatabaseRequest(projectId);
          if (responseStatus == 200) {
            success = true;
            return;
          }

          if (!responseStatus) {
            this.modalService.showMesssageModal("Calculation Database",
              "Error deleting the Calculation Database.");
            return;
          }

          if (responseStatus == 409) {
            this.modalService.showMesssageModal("Calculation Database",
              "Cannot delete the Calculation Database when calculations are running on the project. Please wait until the calculations are done.");
          }
        });
    return success;
  }

  async operationAllowedByCalculationManager(alternativeMessage?: boolean): Promise<boolean> {
    if (await this.isUserPerformingCalculations(this.currentConfiguration.projectId)) {
      this.showCalculationsOnGoingErrorMessage();
      return false;
    }
    return true;
  }

  showCalculationsOnGoingErrorMessage(alternativeMessage: boolean = false) {
    this.modalService.showMesssageModal("Calculations",
      `Cannot ${alternativeMessage ? "perform operation" : "change values"} while Calculations are ongoing. ` +
      `Please wait until the calculations are done${alternativeMessage ? "" : " or press the cancel button"}.`);
  }

  async saveConfigurationPopup(form: FormGroup | NgForm, deleteCalculationsDatabase: boolean = false): Promise<void> {
    if (this.currentConfiguration.isEqual(this.tempConfiguration)) return;

    if (!(await this.operationAllowedByCalculationManager())) {
      return;
    }

    if (deleteCalculationsDatabase) {
      const success = await this.deleteCalculationsDatabase();
      if (!success) {
        this.cancelConfigurationPopup();
        return;
      }
    }



    this.uiBlock.startBlock("Saving configuration...");
    const config = ConfigurationModel.mapToService(this.currentConfiguration);

    this.saveConfiguration(config).subscribe(
      (data) => {
        const previousLayers: ConfigurationLayerModel[] = this.currentConfiguration.clone().layers;
        this.currentConfiguration = ConfigurationModel.map(data);
        this.currentConfiguration.layers = previousLayers;
        this.updateCoordenateControlRCSList();
        if(config.id == null){ // NEW CONFIG
          this.configurationList = [];
          this.getAllConfigurations(config.projectId).subscribe(
            (data) => {
              data.forEach(el => {
                this.configurationList.push(new ConfigurationModel(el));
              });
            },
            (error) => {
              this.modalService.showMesssageModal("Error", "Unable to update configurations list.");
            }
          );
          this.uiBlock.stopBlock();
          this.modalService.showMesssageModal("Configuration created", "Configuration was created successfully!");
        }
        else{
          this.uiBlock.stopBlock();
          this.modalService.showMesssageModal("Configuration saved", "Configuration was saved successfully!");
        }
        this.updateChanges();
      }, (error) => {
        this.uiBlock.stopBlock();
        this.modalService.showMesssageModal("Error", "An error occurred on saving the configuration!");
      },
      () => {
        if (form != null) form.reset();
      })
  }

  public saveConfiguration(configuration: any): Observable<any>{
      if (configuration.id == null) {
        return this.http
          .post<any>(
            this.endpointService.apiUrlConfiguration,
            configuration,
            this.endpointService.httpOptions
          );
      } else {
        return this.http
          .put<any>(
            this.endpointService.apiUrlConfiguration,
            configuration,
            this.endpointService.httpOptions
          );
      }
  }

  convertNavaidDates(nav : NavaidModel){
    nav.infoupdate = nav.infoupdate != null? moment(nav.infoupdate).format('yyyy-MM-DD') : null;
    nav.operationalsince = nav.operationalsince != null? moment(nav.operationalsince).format('yyyy-MM-DD') : null;
    nav.plannedremoval = nav.plannedremoval != null? moment(nav.plannedremoval).format('yyyy-MM-DD') : null;
    nav.dueforreplacement = nav.dueforreplacement != null? moment(nav.dueforreplacement).format('yyyy-MM-DD') : null;
    nav.vordueforreplace = nav.vordueforreplace != null? moment(nav.vordueforreplace).format('yyyy-MM-DD') : null;
    nav.voroperationalsince = nav.voroperationalsince != null? moment(nav.voroperationalsince).format('yyyy-MM-DD') : null;
    nav.vorplannedremoval = nav.vorplannedremoval != null? moment(nav.vorplannedremoval).format('yyyy-MM-DD') : null;
  }

  saveConfigLists(configuration: ConfigurationModel): Observable<any>{
    configuration.navaids.forEach( nav => {
      this.convertNavaidDates(nav);
    })
    return this.http.put<any>(
      this.endpointService.apiUrlConfiguration + '/lists',
      configuration,
      this.endpointService.httpOptions
    );
  }

  saveChangesState(): void{
    this.tempConfiguration = this.currentConfiguration.clone();
  }

  updateChanges(): void{
    //TODO CHANGE TO FORMCONTROL
    this.tempConfiguration = this.currentConfiguration.clone();
  }

  redoChanges(): void{
    //TODO CHANGE TO FORMCONTROL
    this.currentConfiguration = this.tempConfiguration.clone();
  }

  exportConfigurationXMLFileHttpRequest(identifier: number){
    return this.http
    .get<Blob>(this.endpointService.endpointDownloadConfiguration + "/" + identifier, {
      headers : this.endpointService.httpOptions.headers,
      responseType: 'blob' as 'json',
    });
  }
}


